/** Dealer role and settings helpers (role_id === 3). */

export const DEALER_ROLE_ID = 3;

export function readStoredLoginDetails() {
	try {
		const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('speed_tech') : null;
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		return parsed && typeof parsed === 'object' ? parsed : null;
	} catch {
		return null;
	}
}

/**
 * @param {Record<string, unknown>} d - settings `data` object
 * @returns {{ roleId: number | null, accountId: string, dealerEntityId: string, displayName: string }}
 */
export function parseSettingsDealerProfile(d) {
	if (!d || typeof d !== 'object') {
		return { roleId: null, accountId: '', dealerEntityId: '', displayName: '' };
	}
	const roleId = d.role_id != null ? Number(d.role_id) : null;
	const accountId = d.id != null && String(d.id).trim() !== '' ? String(d.id).trim() : '';
	const dealerEntityId =
		d.dealer_id != null && String(d.dealer_id).trim() !== '' ? String(d.dealer_id).trim() : '';
	const displayName =
		(d.name && String(d.name).trim()) || (d.user_name && String(d.user_name).trim()) || '';
	return { roleId, accountId, dealerEntityId, displayName };
}

/** Param for `availableDealerCameras`: settings account `id` only (not dealer entity id). */
export function resolveAccountIdForAvailableDealerCameras(settingsAccountId) {
	const fromSettings = String(settingsAccountId || '').trim();
	if (fromSettings) return fromSettings;
	const ls = readStoredLoginDetails();
	if (ls && ls.id != null && String(ls.id).trim() !== '') {
		return String(ls.id).trim();
	}
	return '';
}

/** If logged-in user is dealer, return { dealerId, displayName } for form `dealer_id` + label. */
export function getDealerFormPrefillFromSettings(d) {
	const p = parseSettingsDealerProfile(d);
	if (p.roleId !== DEALER_ROLE_ID || !p.dealerEntityId) return null;
	return { dealerId: p.dealerEntityId, displayName: p.displayName || 'Dealer' };
}

export function isDealerRoleId(roleId) {
	return Number(roleId) === DEALER_ROLE_ID;
}

/** Payment fields sent when dealer skips payment UI (amounts zero, payment_type empty). */
export function getDealerZeroPaymentPayload() {
	return {
		amount: 0,
		payment_type: '',
		cash_amount: 0,
		online_amount: 0,
		payment_id: '',
		total_amount: 0,
	};
}

export function appendDealerZeroPaymentToFormData(formData) {
	const z = getDealerZeroPaymentPayload();
	formData.append('amount', z.amount);
	formData.append('payment_type', z.payment_type);
	formData.append('cash_amount', z.cash_amount);
	formData.append('online_amount', z.online_amount);
	formData.append('payment_id', z.payment_id);
}

/** API list rows use `camera` for camera 1; forms use `camera_name`. */
export function getDealerCameraNameFromRow(row) {
	if (!row || typeof row !== 'object') return '';
	return String(row.camera_name ?? row.camera ?? '').trim();
}

export const MAX_VISIBLE_CAMERAS = 5;

export function clampCameraCount(n) {
	const v = parseInt(String(n ?? '').trim(), 10);
	if (Number.isNaN(v) || v < 1) return 0;
	return Math.min(MAX_VISIBLE_CAMERAS, v);
}

export function inferDealerCameraCountFromRow(row) {
	if (!row || typeof row !== 'object') return 0;
	if (row.no_of_cameras != null && String(row.no_of_cameras).trim() !== '') {
		const fromRow = parseInt(String(row.no_of_cameras), 10);
		if (!Number.isNaN(fromRow) && fromRow >= 1) {
			return Math.min(MAX_VISIBLE_CAMERAS, fromRow);
		}
	}
	let n = 0;
	if (getDealerCameraNameFromRow(row)) n = 1;
	if (String(row.camera_2 ?? '').trim()) n = 2;
	if (String(row.camera_3 ?? '').trim()) n = 3;
	if (String(row.camera_4 ?? '').trim()) n = 4;
	if (String(row.camera_5 ?? '').trim()) n = 5;
	return n;
}

/**
 * @param {Record<string, unknown>} row
 * @returns {{ camera_name: string, camera_2: string, camera_3: string, camera_4: string, camera_5: string, count: number }}
 */
export function mapDealerCameraRowToFormFields(row) {
	const camera_name = getDealerCameraNameFromRow(row);
	const camera_2 = String(row?.camera_2 ?? '').trim();
	const camera_3 = String(row?.camera_3 ?? '').trim();
	const camera_4 = String(row?.camera_4 ?? '').trim();
	const camera_5 = String(row?.camera_5 ?? '').trim();
	const count = inferDealerCameraCountFromRow({
		...row,
		camera_name,
		camera_2,
		camera_3,
		camera_4,
		camera_5,
	});
	return { camera_name, camera_2, camera_3, camera_4, camera_5, count };
}

export function parseVisibleCameraCount(noOfCameras) {
	return clampCameraCount(noOfCameras);
}

/** Resolve display count from job record (`no_of_cameras` or filled camera slots). */
export function resolveNoOfCamerasFromJob(job) {
	if (!job || typeof job !== 'object') return '';
	if (job.no_of_cameras != null && String(job.no_of_cameras).trim() !== '') {
		const n = parseVisibleCameraCount(job.no_of_cameras);
		if (n) return String(n);
	}
	const count = inferDealerCameraCountFromRow(job);
	return count > 0 ? String(count) : '';
}

export function trimCamerasOnTarget(target, count) {
	if (!target || typeof target !== 'object') return;
	if (count < 5) target.camera_5 = '';
	if (count < 4) target.camera_4 = '';
	if (count < 3) target.camera_3 = '';
	if (count < 2) target.camera_2 = '';
	if (count < 1) target.camera_name = '';
}

export function applyMappedCamerasToTarget(target, row, count) {
	const mapped = mapDealerCameraRowToFormFields(row);
	const n = Math.min(MAX_VISIBLE_CAMERAS, Math.max(1, count || mapped.count || 1));
	if (n >= 1) target.camera_name = mapped.camera_name;
	if (n >= 2) target.camera_2 = mapped.camera_2;
	if (n >= 3) target.camera_3 = mapped.camera_3;
	if (n >= 4) target.camera_4 = mapped.camera_4;
	if (n >= 5) target.camera_5 = mapped.camera_5;
	trimCamerasOnTarget(target, n);
	return n;
}

const CAMERA_FIELD_LABELS = [
	{ key: 'camera_name', label: 'Camera Name' },
	{ key: 'camera_2', label: 'Camera 2' },
	{ key: 'camera_3', label: 'Camera 3' },
	{ key: 'camera_4', label: 'Camera 4' },
	{ key: 'camera_5', label: 'Camera 5' },
];

/** @returns {string | null} error message or null if valid */
export function validateVisibleCameraFields(values, visibleCount) {
	const n = parseVisibleCameraCount(visibleCount);
	if (!n) return `Please enter no of cameras (1 to ${MAX_VISIBLE_CAMERAS})`;
	for (let i = 0; i < n; i++) {
		const { key, label } = CAMERA_FIELD_LABELS[i];
		if (!String(values[key] ?? '').trim()) {
			return `Please enter ${label}`;
		}
	}
	return null;
}
